import styled from "@emotion/styled";
import { padding } from "polished";
import React, { useEffect, useRef } from "react";

import { ResponsiveIcon } from "@brexhq/next/src/components/shared/Icon";
import { Link } from "@brexhq/next/src/components/shared/Link";

import { NAV_HEIGHT } from "../../consts";

export { Backdrop } from "./Backdrop";
export { NavLink } from "./NavLink";
export { NavMenu } from "./NavMenu";
export { Submenu } from "./Submenu";
export { DesktopNavLoadingStencil } from "./LoadingStencil";

export const HeaderContainer = styled.div(({ theme }) => ({
  zIndex: theme.zIndex.HEADER,
  position: "sticky",
  top: 0,
  background: theme.colors.background.primary,
  // Using box shadow as border bottom so we don't have a 1px gap for hover state
  boxShadow: `0 1px 0 0 ${theme.colors.border.default}`,
}));

export const Nav = styled.nav(({ theme }) => ({
  ...padding(0, theme.spacing.LRG.sml),

  background: theme.colors.background.primary,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  color: theme.colors.content.primary,
  // Using box shadow as border bottom so we don't have a 1px gap for hover state
  height: NAV_HEIGHT,
  maxWidth: theme.maxWidths.moduleGrid,
  margin: "0 auto",
}));

interface NavItemListProps {
  /**
   * Lets the navigation take 100% of the remaining width available to prevent NavMenus from closing when hovering the navigation
   */
  isFullWidth?: boolean;
}

export const NavItemList = styled.ul<NavItemListProps>(({ isFullWidth }) => ({
  display: "flex",
  alignItems: "center",
  listStyleType: "none",
  height: "100%",
  padding: 0,
  margin: 0,
  gap: 20,
  ...(isFullWidth && { width: "100%" }),
}));

export const NavItem = ({ children }) => <li role="none">{children}</li>;

interface LogoProps {
  onActivate: (item: string) => void;
  isFocused?: boolean;
  tabIndex?: number;
  disableLink?: boolean;
}

export const Logo = ({
  isFocused,
  tabIndex,
  onActivate,
  disableLink,
}: LogoProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (isFocused) {
      linkRef.current?.focus();
    }
  }, [isFocused]);
  const icon = (
    <ResponsiveIcon
      icon="RebirthLogo"
      responsive={{
        LRG: { width: 78, height: 20 },
        MED: { width: 78, height: 20 },
        SML: { width: 92, height: 24 },
      }}
    />
  );
  return disableLink ? (
    icon
  ) : (
    <Link
      ref={linkRef}
      aria-label="Brex logo"
      role="menuitem"
      modifier="unstyled"
      component={"nav"}
      section="logo"
      to="/"
      label="Brex logo"
      tabIndex={tabIndex}
      onFocus={() => onActivate("logo")}
      onMouseOver={() => onActivate("logo")}
    >
      {icon}
    </Link>
  );
};
